import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useAccess } from '@hooks/access';
import { snakeToCamelCase } from '@utils/textHelpers';
import { SchedulerSidebarResourcesRequirements } from '@components/scheduler/sidebar/requirements';
import { requirement as Requirement } from '@components/scheduler/sidebar/requirements/types';
import { defaultCrewRequirement } from '@components/scheduler/sidebar/crew/constants';
import { sortByName } from '@components/scheduler/sidebar/crew/helpers';
import { SchedulerSidebarCrewMember } from '@contexts/scheduler/sidebar/types';
import { PhoneSection } from '../section';
import { getConflicts } from '../multiDay/helpers';
import { ResourcePicker } from '../resourcePicker';
import { ResourceRow } from '../resourceRow';
import { PhoneResourcesProps } from './types';

export const PhoneCrew = ({
  open,
  onOpenChange,
  swapId,
  onSwap,
  multi,
  specialty,
}: PhoneResourcesProps) => {
  const { t } = useTranslation('app');
  const { t: tPrimaryRoles } = useTranslation('app', { keyPrefix: 'primaryRole' });
  const access = useAccess();
  const [collapsed, setCollapsed] = useState(false);
  const [requirement, setRequirement] = useState<Requirement>();
  const {
    crewMembers,
    disabled,
    setState,
    conflictsByUserId,
    currentSchedule: {
      task: { requirements },
    },
  } = useSchedulerSidebar();
  const {
    active: { schedule },
  } = useSchedulerContext();
  const {
    crewMembers: { options: crewOptions, disabled: crewDisabled },
    fieldUsers: { options: fieldUserOptions, disabled: fieldUsersDisabled },
  } = useDropdownsContext();
  const assigned = crewMembers.filter((el) => !el.discardedAt && !el.userDeactivated);
  const taskRequirements =
    schedule?.id === 'new' ? [defaultCrewRequirement(assigned.length)] : requirements;
  const readOnly = access.readOnly || disabled;
  const multiConflicts = (id: string, name: string) => {
    if (!multi) return {};
    const hits = getConflicts(multi.lookup, 'users', id);
    return {
      conflictDays: new Set(hits.map((hit) => hit.date)).size,
      onShowConflicts: () => multi.onShowConflicts(name, hits),
    };
  };

  useEffect(() => {
    const crewRequirement = requirements?.find((el) => el.requirementType === 'crew_member');
    if (!crewRequirement) return;
    setRequirement({ ...crewRequirement, currentlyAssigned: assigned.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements, crewMembers]);

  const users = useMemo(
    () =>
      [...crewOptions, ...fieldUserOptions].filter(
        (el) => el.id !== 'unassigned' && el.id !== schedule.foreman?.id && !el.discardedAt,
      ),
    [crewOptions, fieldUserOptions, schedule.foreman?.id],
  );
  const options = sortByName(
    users.map((el) => ({
      value: el.id,
      label: el.fullName || '',
      sublabel: el.primaryRole ? tPrimaryRoles(snakeToCamelCase(String(el.primaryRole))) : '',
      conflicts: multi ? [] : conflictsByUserId[el.id] || [],
      qualified: !specialty || Boolean(el.specialtyValues?.includes(specialty)),
      ...multiConflicts(el.id, el.fullName || ''),
    })),
  );
  const selected = crewMembers.filter((el) => el.userId && !el.discardedAt).map((el) => el.userId);

  const toMember = (userId: string): SchedulerSidebarCrewMember => {
    const user = users.find((el) => el.id === userId);
    return {
      id: undefined,
      userId,
      name: user?.fullName || '',
      primaryRole: user?.primaryRole || undefined,
      conflicts: [],
      roles: user?.roles.map((el) => ({ ...el, id: '' })) || [],
      edit: false,
    };
  };

  const toggle = (userId: string) => {
    if (swapId) {
      if (userId !== swapId && !selected.includes(userId))
        setState((prev) => ({
          ...prev,
          defaultCrew: false,
          crewMembers: prev.crewMembers.map((el) => (el.userId === swapId ? toMember(userId) : el)),
        }));
      onSwap(undefined);
      onOpenChange(false);
      return;
    }
    setState((prev) => ({
      ...prev,
      defaultCrew: false,
      crewMembers: selected.includes(userId)
        ? prev.crewMembers.filter((el) => el.userId !== userId)
        : [...prev.crewMembers, toMember(userId)],
    }));
  };

  const remove = (userId: string) =>
    setState((prev) => ({
      ...prev,
      defaultCrew: false,
      crewMembers: prev.crewMembers.filter((el) => el.userId !== userId),
    }));

  return (
    <PhoneSection
      title={
        <span className="flex items-center gap-2 [&>div]:justify-start [&_strong]:text-sm [&_strong]:font-bold">
          <SchedulerSidebarResourcesRequirements
            scheduleStatus="draft"
            requirement={requirement || defaultCrewRequirement(assigned.length)}
            onChangeAmount={(value) =>
              setState((prev) => ({
                ...prev,
                currentSchedule: {
                  ...prev.currentSchedule,
                  task: {
                    ...prev.currentSchedule.task,
                    requirements: taskRequirements?.map((el) =>
                      el.id === requirement?.id ? { ...el, totalNeeded: value } : el,
                    ),
                  },
                },
              }))
            }
          />
        </span>
      }
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}>
      {!collapsed && (
        <div className="flex flex-col gap-1.5">
          {!access.readOnly && (
            <ResourcePicker
              open={open}
              onOpenChange={(value) => {
                if (!value) onSwap(undefined);
                onOpenChange(value);
              }}
              options={options}
              selected={swapId ? [swapId] : selected}
              swapping={Boolean(swapId)}
              onToggle={toggle}
              addLabel={t('schedule.crew.add')}
              searchPlaceholder={t('schedule.crew.searchPlaceholder')}
              hint={t('schedule.crew.updateHint')}
              conflictTitle={t('schedule.crew.tooltip.conflict.title')}
              disabled={readOnly || crewDisabled || fieldUsersDisabled}
            />
          )}
          {crewMembers.map((el) => (
            <ResourceRow
              key={el.userId}
              name={el.name}
              role={el.roles
                ?.map((role) => t(`role.${snakeToCamelCase(String(role.name || ''))}`))
                .join(', ')}
              conflicts={el.userDeactivated || multi ? [] : (conflictsByUserId[el.userId] ?? [])}
              {...multiConflicts(el.userId, el.name)}
              deactivated={el.userDeactivated}
              disabled={readOnly}
              swapLabel={t('schedule.crew.tooltip.replace')}
              removeLabel={t('schedule.crew.tooltip.remove')}
              deactivatedLabel={t('scheduling.phone.deactivated')}
              onSwap={() => {
                onSwap(el.userId);
                onOpenChange(true);
              }}
              onRemove={() => remove(el.userId)}
            />
          ))}
        </div>
      )}
    </PhoneSection>
  );
};
